/**
 * Where to send someone after they log in.
 *
 * WHY this exists: every link in our notification emails points at a deep page
 * (/predict/<id>), but a reader whose token has expired was sent to /login and then dropped on
 * the map page — the listing they clicked was gone. Three places bounce to login (PrivateRoute,
 * AdminRoute, and authFetch's 401 handler) and two return from it (password login and the Google
 * OAuth callback), so the "remember where they were going" logic lives here rather than five
 * times over.
 *
 * The destination travels as a `next` QUERY PARAM, not react-router state: authFetch's 401
 * handler uses window.location.href, which tears down the SPA, and the Google flow leaves our
 * origin entirely. Neither survives in-memory state.
 */

const KEY = "postLoginNext";

/**
 * The destination, latched for this page load.
 *
 * consumeStashedNext() deletes as it reads, which made it unsafe to call twice: React
 * StrictMode invokes effects twice in development, so LoginPage's callback read the parked
 * path, navigated to it, then ran again, got null, and navigated to "/" — the second one won
 * and the deep link was lost. Latching means every call after the first returns the same
 * answer, so a remount (StrictMode or otherwise) cannot change where the reader lands.
 * `undefined` = not read yet this page load; `null` = read, nothing parked.
 */
let latched;

/** Paths that must never be a post-login destination (you'd bounce straight back). */
const NEVER = ["/login", "/logout"];

/**
 * Sanitise an untrusted `next` value into a same-origin path, or null.
 *
 * Only site-relative paths are allowed: a value starting "//" or "https://" would make ?next= an
 * open redirect, letting a crafted link send someone from our login page to an attacker's.
 */
export function safeNext(raw) {
  if (!raw) return null;
  let value = raw;
  try {
    value = decodeURIComponent(raw);
  } catch (e) {
    return null; // malformed percent-encoding
  }
  if (!value.startsWith("/")) return null;      // absolute URL or scheme-relative
  if (value.startsWith("//")) return null;      // protocol-relative -> another host
  if (NEVER.some((p) => value === p || value.startsWith(`${p}?`))) return null;
  return value;
}

/** The current location as a `next` value: path + query, no origin. */
export function currentPath(location) {
  const loc = location || window.location;
  return `${loc.pathname}${loc.search || ""}`;
}

/** "/login?next=..." for a location, or plain "/login" when there's nothing worth keeping. */
export function loginUrlFor(location) {
  const next = safeNext(currentPath(location));
  return next ? `/login?next=${encodeURIComponent(next)}` : "/login";
}

/**
 * Park a destination across a redirect that leaves our origin (the Google OAuth round-trip goes
 * to the backend and comes back with only ?token=, so a query param cannot survive it).
 */
export function stashNext(next) {
  const safe = safeNext(next);
  latched = undefined; // a new destination invalidates anything latched earlier
  try {
    if (safe) sessionStorage.setItem(KEY, safe);
    else sessionStorage.removeItem(KEY);
  } catch (e) {
    /* private mode / storage disabled — fall back to the default landing page */
  }
}

/**
 * Read a parked destination, clearing it from storage. Returns null when there isn't one.
 * Idempotent for the rest of the page load — see `latched`.
 */
export function consumeStashedNext() {
  if (latched !== undefined) return latched;
  try {
    const value = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    latched = safeNext(value);
  } catch (e) {
    latched = null;
  }
  return latched;
}
